import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Keycloak from "keycloak-js";
import { useNavigate } from "react-router-dom";
import Logger from "./utils/Logger";
import httpClient from "./services/httpClient";

export const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";
export const FLASK_URL =
  process.env.REACT_APP_FLASK_URL || "http://localhost:5000";
export const KEYCLOAK_URL =
  process.env.REACT_APP_KEYCLOAK_URL || "http://localhost:8080";
export const REACT_URL =
  process.env.REACT_APP_REACT_URL || "http://localhost:3000";

const AuthContext = createContext();

const keycloakConfig = {
  url: KEYCLOAK_URL,
  realm: "flask-demo",
  clientId: "react-app-client",
};

const DEPARTMENT_ROLES = ["Student", "Employee", "DeputyManager", "Manager"];

const mapUserRole = (roles = []) => {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("hr-manager")) return "hr-manager";
  return "user";
};

const fetchUserDepartmentRoles = async (userId) => {
  if (!userId) {
    console.error("No user ID found");
    return [];
  } else {
    console.log("User ID found:", userId);
  }

  const roleChecks = await Promise.all(
    DEPARTMENT_ROLES.map(async (role) => {
      try {
        const response = await httpClient.get(`/Users/role/${role}`, {
          withAuth: true,
        });
        const users = response.data || [];
        return users.some((user) => user.id === userId) ? role : null;
      } catch (error) {
        Logger.error(`Error fetching users with role ${role}:`, error);
        return null;
      }
    }),
  );

  return roleChecks.filter(Boolean);
};

const buildUserData = async (data) => {
  const userRoles = await fetchUserDepartmentRoles(data.userId);

  return {
    userName: data.username,
    userRole: mapUserRole(data.roles),
    userEmail: data.email,
    userId: data.userId,
    userRoles,
  };
};

export const AuthProvider = ({ children }) => {
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [userData, setUserData] = useState({
    userName: "",
    userRole: "",
    userEmail: "",
    userId: "",
    userRoles: [],
  });

  const navigate = useNavigate();
  const navigated = useRef(false); // Prevent double navigation
  const initialized = useRef(false); // Prevent double Keycloak initialization
  const isLoggingOut = useRef(false);

  const isTokenValid = (keycloakInstance) => {
    if (!keycloakInstance || !keycloakInstance.token) {
      return false;
    }

    try {
      const tokenExp = keycloakInstance.tokenParsed?.exp;
      if (!tokenExp) return false;

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenExp - currentTime;

      // Uważaj token za nieważny jeśli zostało mniej niż 2 minuty
      const isValid = timeUntilExpiry > 120;

      if (!isValid) {
        Logger.log(
          `Token expires in ${timeUntilExpiry} seconds, marking as invalid`,
        );
      }

      return isValid;
    } catch (error) {
      Logger.error("Error checking token validity:", error);
      return false;
    }
  };

  const refreshToken = async (keycloakInstance) => {
    try {
      // Sprawdź czy token jeszcze jest ważny
      if (isTokenValid(keycloakInstance)) {
        Logger.log("Token is still valid, no need to refresh.");
        return true;
      }

      Logger.log("Attempting to refresh token...");
      const refreshed = await keycloakInstance.updateToken(30);

      if (refreshed && keycloakInstance.token) {
        Logger.log("Token refreshed successfully");
        localStorage.setItem("token", keycloakInstance.token);

        // Pobierz zaktualizowane dane użytkownika
        try {
          const response = await httpClient.get("/auth", {
            withAuth: true,
          });
          const data = response.data;

          setUserData(await buildUserData(data));

          return true;
        } catch (roleError) {
          Logger.error("Failed to fetch updated user data:", roleError);
          // Nie logujemy użytkownika za błąd pobierania roli
          return true;
        }
      } else {
        Logger.warn("Token refresh failed - no new token received");
        return false;
      }
    } catch (error) {
      Logger.error("Failed to refresh token:", error);

      // Sprawdź czy to błąd refresh tokena
      if (error.error === "invalid_grant") {
        Logger.error("Refresh token expired - logging out");
        logout();
        return false;
      }

      // Dla innych błędów, spróbuj ponownie później
      return false;
    }
  };

  const setupTokenRefresh = (keycloakInstance) => {
    return setInterval(async () => {
      const success = await refreshToken(keycloakInstance);
      if (!success) {
        Logger.warn("Token refresh failed, will retry in next interval");
      }
    }, 30000); // Sprawdzaj co 30 sekund zamiast co 10
  };

  useEffect(() => {
    // Prevent double initialization
    if (initialized.current) return;
    initialized.current = true;

    const keycloakInstance = new Keycloak(keycloakConfig);
    let refreshInterval;

    keycloakInstance
      .init({
        onLoad: "login-required",
        checkLoginIframe: false,
      })
      .then((auth) => {
        setKeycloak(keycloakInstance);

        // Udostępnij instancję Keycloak globalnie dla httpClient
        window.keycloakInstance = keycloakInstance;

        if (auth) {
          const { token: accessToken, refreshToken: keycloakRefreshToken } =
            keycloakInstance;

          if (accessToken && keycloakRefreshToken) {
            Logger.log("Tokens", {
              accessToken,
              refreshToken: keycloakRefreshToken,
            });

            localStorage.setItem("token", accessToken);

            // Dodaj event listenery dla Keycloak
            keycloakInstance.onTokenExpired = () => {
              Logger.warn("Token expired, attempting refresh...");
              refreshToken(keycloakInstance);
            };

            keycloakInstance.onAuthRefreshSuccess = () => {
              Logger.log("Auth refresh successful");
              localStorage.setItem("token", keycloakInstance.token);
            };

            keycloakInstance.onAuthRefreshError = () => {
              Logger.error("Auth refresh failed");
              logout();
            };

            keycloakInstance.onAuthLogout = () => {
              Logger.log("User logged out");
              localStorage.removeItem("hasLoggedIn");
              localStorage.removeItem("token");
              isLoggingOut.current = false;
            };

            httpClient
              .get("/auth", { withAuth: true })
              .then((response) => response.data)
              .then(async (data) => {
                Logger.log("User data received:", data);

                setUserData(await buildUserData(data));
                setAuthenticated(true);

                refreshInterval = setupTokenRefresh(keycloakInstance);
              })
              .catch((err) => {
                Logger.error("Error fetching user data:", err);
                setAuthenticated(false);
              });
          } else {
            Logger.error("Invalid token values", {
              accessToken,
              refreshToken: keycloakRefreshToken,
            });
            setAuthenticated(false);
          }
        } else {
          setAuthenticated(false);
        }
      })
      .catch((err) => {
        if (err) {
          Logger.error("Failed to initialize Keycloak:", err);
        } else {
          Logger.warn("Failed to initialize Keycloak without an error object");
        }
      });

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation effect
  // Navigation effect
  useEffect(() => {
    if (!navigated.current && userData.userRole) {
      navigated.current = true;

      // Only redirect if user lands at root or unintended default page
      const currentPath = window.location.pathname;

      const shouldRedirect =
        currentPath === "/" || currentPath === "/aktualnosci"; // adjust this list if needed

      Logger.log("Navigation triggered", {
        path: currentPath,
        role: userData.userRole,
        shouldRedirect,
      });

      if (shouldRedirect) {
        switch (userData.userRole) {
          case "admin":
          case "hr-manager":
            navigate("/administrator/aktualnosci", { replace: true });
            break;
          case "user":
            navigate("/aktualnosci", { replace: true });
            break;
          case "guest":
            navigate("/guest-dashboard", { replace: true });
            break;
          default:
            Logger.error("Unknown role:", userData.userRole);
            navigate("/aktualnosci", { replace: true });
        }
      }
    }
  }, [userData.userRole, navigate]);

  const logout = () => {
    if (isLoggingOut.current) {
      return;
    }

    isLoggingOut.current = true;
    localStorage.removeItem("hasLoggedIn");
    localStorage.removeItem("token");
    if (keycloak) {
      keycloak.logout({ redirectUri: REACT_URL });
    } else {
      window.location.href = REACT_URL;
    }
  };

  const getToken = () => keycloak?.token || null;

  return (
    <AuthContext.Provider
      value={{ keycloak, authenticated, userData, logout, getToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
